<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { twMerge } from "tailwind-merge";
import { useForm } from "vee-validate";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import * as yup from "yup";

import { postClaimPurchase } from "@/api";
import { useReceipt } from "@/composables/api/useReceipt";
import { useUser } from "@/composables/api/useUser";

import ClaimDialog from "@/components/sections/receipt-details/claim/ClaimDialog.vue";
import ConfirmClaimDialog from "@/components/sections/receipt-details/claim/ConfirmClaimDialog.vue";
import EmptyProductList from "@/components/sections/receipt-details/EmptyProductList.vue";
import ProductList from "@/components/sections/receipt-details/ProductList.vue";
import ReceiptBanner from "@/components/sections/receipt-details/ReceiptBanner.vue";
import ReceiptTitle from "@/components/sections/receipt-details/ReceiptTitle.vue";
import Button from "@/components/ui/Button.vue";
import Spinner from "@/components/ui/Spinner.vue";

const { data: user } = useUser();

const { t } = useI18n();
const route = useRoute();
const queryClient = useQueryClient();
const { data: receipt, isLoading } = useReceipt(route.params.id as string);

const missingPointsForm = useForm<{ missingPoints: string[] }>({
  initialValues: {
    missingPoints: [],
  },
  validationSchema: yup.object({
    missingPoints: yup
      .array()
      .of(yup.string())
      .min(1, "Veuillez sélectionner au moins 1 élément")
      .required(),
  }),
});

const claimMode = ref<"missingPoints" | "missingProduct" | "other" | null>(null);
const isSubmitting = ref(false);

const isClaimDialogOpen = ref(false);
const isConfirmClaimDialogOpen = ref(false);

const isClaimingMissingPoints = computed(() => claimMode.value === "missingPoints");

function continueClaim() {
  claimMode.value = null;
  isConfirmClaimDialogOpen.value = false;
  openClaimDialog();
}

function confirmClaims() {
  claimMode.value = null;
  isConfirmClaimDialogOpen.value = false;
}

function openClaimDialog() {
  isClaimDialogOpen.value = true;
}

function openOtherClaimDialog() {
  claimMode.value = "other";
  openClaimDialog();
}

function cancelClaimPoints() {
  claimMode.value = null;
}

function claimSubmitted() {
  isClaimDialogOpen.value = false;
  // Only open ConfirmClaimDialog for non-completed receipts
  if (receipt.value?.status !== "completed" && receipt.value?.status !== "rejected") {
    isConfirmClaimDialogOpen.value = true;
  }
}

const handleSubmit = missingPointsForm.handleSubmit(async (values) => {
  const receiptId = route.params.id;

  if (isSubmitting.value || typeof receiptId !== "string" || !values.missingPoints?.length) {
    return;
  }

  isSubmitting.value = true;

  try {
    // Ensure missingPoints is always treated as an array
    const promises = [values.missingPoints]
      .flat()
      .map((lineId) => {
        const matchingLine = receipt.value?.lines.find((l) => l.id === lineId);

        // Ensure the line exists in the receipt.
        return matchingLine ? postClaimPurchase(receiptId, lineId) : false;
      })
      .filter(Boolean);

    await Promise.all(promises);

    queryClient.invalidateQueries({ queryKey: ["receipt", receiptId] });
    toast(t("toast.claimMissingProductPointsSuccess"), { position: "top-center" });

    isConfirmClaimDialogOpen.value = true;
  } catch (error) {
    console.error(error);
  }

  isSubmitting.value = false;
});
</script>

<template>
  <main class="relative flex w-full flex-col" role="main" aria-labelledby="ticket-title">
    <template v-if="isLoading || !receipt">
      <div class="flex h-64 w-full items-center justify-center bg-[#D4DBEC]" role="banner">
        <Spinner class="size-24" />
      </div>
    </template>

    <template v-else>
      <ReceiptBanner
        :previewImageSrc="receipt.preview_image_url"
        :fullImageSrc="receipt.image_url"
        :created="receipt.created"
        :status="receipt.status"
      />

      <div
        class="relative mx-auto flex w-full max-w-2xl flex-col gap-12 overflow-x-hidden px-6 pt-5 pb-16 lg:gap-20 lg:pt-12 lg:pb-24"
      >
        <ReceiptTitle :receipt="receipt" />

        <section
          v-if="receipt.status !== 'rejected'"
          class="flex w-full flex-col gap-3 lg:gap-6"
          aria-labelledby="products-title"
        >
          <h2
            id="products-title"
            :class="
              twMerge(
                'text-galbani text-lg font-semibold lg:text-2xl',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue',
              )
            "
          >
            {{ $t("receipt.products.title") }}
          </h2>

          <EmptyProductList v-if="receipt.status === 'declared' || receipt.lines.length === 0" />

          <ProductList
            v-else
            :lines="receipt.lines"
            :isClaimingMissingPoints="isClaimingMissingPoints"
          />
        </section>

        <!-- Desktop-only claim actions. -->
        <div
          v-if="isClaimingMissingPoints"
          class="hidden w-full items-center justify-center gap-6 lg:flex"
        >
          <Button variant="secondary" @click="handleSubmit">
            <template v-if="!isSubmitting">
              {{ $t("receipt.claim.confirm") }}
            </template>

            <Spinner
              v-else
              class="size-7"
              trackColor="rgba(255, 255, 255, 0.3)"
              color="rgba(255, 255, 255, 1)"
            />
          </Button>

          <Button variant="primary" @click="cancelClaimPoints">
            {{ $t("receipt.claim.cancel") }}
          </Button>
        </div>

        <Button
          v-if="receipt.status === 'filtered_analyzed' && !isClaimingMissingPoints"
          variant="secondary"
          class="mx-auto hidden max-w-sm lg:flex"
          @click="openClaimDialog"
        >
          {{ $t("dialog.claim.title") }}
        </Button>

        <Button
          v-if="receipt.status === 'completed' || receipt.status === 'rejected'"
          variant="secondary"
          class="mx-auto hidden max-w-sm lg:flex"
          @click="openOtherClaimDialog"
        >
          {{ $t("dialog.claim.title") }}
        </Button>
      </div>

      <!-- Mobile-only claim actions. -->
      <div
        v-if="
          isClaimingMissingPoints ||
          (receipt.status === 'filtered_analyzed' && !isClaimingMissingPoints) ||
          receipt.status === 'completed' ||
          receipt.status === 'rejected'
        "
        class="fixed right-0 bottom-0 left-0 z-[1] flex w-full items-center justify-center gap-4 bg-black py-3 lg:hidden"
      >
        <template v-if="isClaimingMissingPoints">
          <Button variant="secondary" @click="handleSubmit">
            <template v-if="!isSubmitting">
              {{ $t("receipt.claim.confirm") }}
            </template>

            <Spinner
              v-else
              class="size-7"
              trackColor="rgba(255, 255, 255, 0.3)"
              color="rgba(255, 255, 255, 1)"
            />
          </Button>

          <Button variant="outline" @click="cancelClaimPoints">
            {{ $t("receipt.claim.cancel") }}
          </Button>
        </template>

        <Button
          v-if="receipt.status === 'filtered_analyzed' && !isClaimingMissingPoints"
          variant="primary"
          class="lg:hidden"
          @click="openClaimDialog"
        >
          {{ $t("dialog.claim.title") }}
        </Button>

        <Button
          v-if="receipt.status === 'completed' || receipt.status === 'rejected'"
          variant="primary"
          class="lg:hidden"
          @click="openOtherClaimDialog"
        >
          {{ $t("dialog.claim.title") }}
        </Button>
      </div>

      <ClaimDialog
        v-model="claimMode"
        v-model:open="isClaimDialogOpen"
        :disabled="receipt.status === 'declared'"
        :receiptStatus="receipt.status"
        @missingProductClaim="claimSubmitted"
        @otherClaim="claimSubmitted"
      />

      <ConfirmClaimDialog
        v-if="receipt.status !== 'completed' && receipt.status !== 'rejected'"
        v-model="isConfirmClaimDialogOpen"
        @continueClaim="continueClaim"
        @confirmClaims="confirmClaims"
      />
    </template>
  </main>
</template>
